import React, { useEffect, useState, useRef } from "react";
import Page from "components/page/Page";
import Card from "components/card/Card";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { loadAdminFacility } from "redux/actions/facility";
import Input from "components/form/input/Input";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import { StatusCode } from "api/protocols";
import "./facility.scss";
import { isEqualWith, isNull } from "lodash";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IFacility } from "redux/reducers/models/facility";
import { faTimes, faUpload } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  GetFacility,
  GetFacilityCurrencies,
  PostFacilityLogo,
  PutFacility,
  PutFacilityCurrency,
} from "api/rpc/2024-04/facilityAdmin/facility/facility";
import moment from "moment";
import Spin from "components/spin/spin";
import axios, { CancelToken } from "axios";
import { postcodeValidator, postcodeValidatorExistsForCountry } from "postcode-validator";

export default function FacilityEdit() {
  const { t } = useTranslation();
  const { Option } = Select;
  const dispatch = useAppDispatch();
  const {
    authStore: {
      user: { permissions },
    },
  } = useAppSelector(store => store);

  const imageUploadInputRef = useRef(null);

  const timeZones = moment.tz.zonesForCountry("CA");
  const business_types = [
    {
      title: t("Sports Clubs / Fields - 7941"),
      value: 7941,
    },
    {
      title: t("Computer Software - 5734"),
      value: 5734,
    },
    {
      title: t("Hotels, Motels, and Resorts - 7011"),
      value: 7011,
    },
  ];

  const [facilityState, setFacilityState] = useState<IFacility>(null);

  const [facilityStateBeforeChanges, setFacilityStateBeforeChanges] = useState<IFacility>(undefined);
  const [facilityStateLoaded, setFacilityStateLoaded] = useState<boolean>(false);
  const [currencies, setCurrencies] = useState<Array<{ id: number; code: string; symbol: string }>>([]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadFacility(source.token);
    return () => source.cancel();
  }, []);

  async function loadFacility(cancelToken: CancelToken) {
    const [facilityRes, currencyRes] = await Promise.all([
      GetFacility(null, false, cancelToken),
      GetFacilityCurrencies(null, false, cancelToken),
    ]);

    if (facilityRes?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken.reason) {
        return;
      }
      dispatch(showError(t("Error getting facility details")));
      return;
    }

    if (currencyRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting currencies")));
    }

    const facility = facilityRes.data[0];

    ReactDOM.unstable_batchedUpdates(() => {
      setFacilityState({ ...facility });
      setFacilityStateLoaded(true);
      setCurrencies(currencyRes?.status === StatusCode.OK ? currencyRes?.data as { id: number; code: string; symbol: string; }[] : []);
    });
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setFacilityState(prevState => ({ ...prevState, [id]: value }));
  }

  async function saveFacility() {
    if (postcodeValidatorExistsForCountry(facilityState.country_code)) {
      if (!postcodeValidator(facilityState.postal, facilityState.country_code)) {
        dispatch(showError(t("Incorrect postal code format")));
        return;
      }
    }
    dispatch(enqueue());
    //Update default currency
    if (facilityState?.default_currency_id !== facilityStateBeforeChanges?.default_currency_id) {
      const currencyRes = await PutFacilityCurrency({ id: facilityState?.default_currency_id, default: true }, false);
      if (currencyRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error updating default currency")));
      }
    }

    const facilityRes = await PutFacility(facilityState, false);
    if (facilityRes.status !== (Number(StatusCode.OK))) {
      dispatch(
        showError(
          typeof facilityRes.data === "string"
            ? facilityRes.data
            : t("Error saving facility"),
        ),
      );
      dispatch(dequeue());
      return;
    }
    //Reset selected facility in redux
    void dispatch(loadAdminFacility({ client: true }, false));
    dispatch(dequeue());
    dispatch(showSuccess(t("Successfully updated facility details")));
    setFacilityStateBeforeChanges(facilityState);
  }

  function unsavedChangesExist() {
    if (facilityStateBeforeChanges === undefined) {
      if (facilityStateLoaded) {
        setFacilityStateBeforeChanges(facilityState);
      }
      return false;
    }

    return !isEqualWith(facilityStateBeforeChanges, facilityState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    setFacilityState(facilityStateBeforeChanges);
  }

  function handleDropDownChange(value: any, key: string) {
    setFacilityState(prevState => ({ ...prevState, [key]: value }));
  }

  function handleTimeZoneChange(value: any) {
    setFacilityState(prevState => ({ ...prevState, timezone: value }));
  }

  async function deleteFacilityImage() {
    const imageRes = await PutFacility({ logo_source: null }, true);
    if (imageRes.status !== (Number(StatusCode.OK))) {
      dispatch(
        showError(
          typeof imageRes?.data === "string" ? imageRes.data : t("Error updating facility image"),
        ),
      );
      return;
    }
    dispatch(showSuccess(t("Successfully updated facility details")));

    const facility = imageRes.data;

    ReactDOM.unstable_batchedUpdates(() => {
      setFacilityStateBeforeChanges(prevState => ({ ...prevState, logo_source: facility?.logo_source }));
      setFacilityState(prev => ({
        ...prev,
        logo_source: facility.logo_source,
      }));
      setFacilityStateLoaded(true);
    });
  }

  function imageUploadOnChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files;

    if (file && file.length > 0) {
      //Upload image after the file is selected
      void uploadFacilityImage(file[0]);
    }
  }

  async function uploadFacilityImage(imageFile: File) {
    const formData = new FormData();
    formData.append("logo_image", imageFile);

    //Reset file selector input value
    imageUploadInputRef.current.value = "";

    const imageRes = await PostFacilityLogo(formData, true);

    if (imageRes.status !== StatusCode.OK) {
      dispatch(
        showError(
          typeof imageRes?.data === "string" ? imageRes.data : t("Error updating facility image"),
        ),
      );
      return;
    }
    dispatch(showSuccess(t("Successfully added facility image")));

    //Update logo source with updated image source from Facility

    ReactDOM.unstable_batchedUpdates(() => {
      setFacilityState(prev => ({
        ...prev,
        logo_source: imageRes.data?.logo_source,
      }));
      setFacilityStateBeforeChanges(prevState => ({ ...prevState, logo_source: imageRes?.data?.logo_source }));
    });
  }

  console.log(
    "%csrc/pages/secure/facility/settings/facility/FacilityEdit.tsx:281 facilityState",
    "color: #007acc;",
    facilityState,
  );
  console.log(
    "%csrc/pages/secure/facility/settings/facility/FacilityEdit.tsx:282 currencies",
    "color: #007acc;",
    currencies,
  );

  return (
    <Page
      title={t("Facility Details")}
      subtitle={t("Edit details about your facility")}
      narrow
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveFacility,
        onCancel: cancelUnsavedChanges,
      }}
    >
      {facilityStateLoaded ? (
        <Form>
          <Card>
            <Card.Section title={t("Facility Details")}>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    value={facilityState.long_name ?? ""}
                    id="long_name"
                    onChange={handleInputChange}
                    placeholder={t("Long name")}
                    label={t("Long name")}
                    disabled={!permissions?.facility_details_edit}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <span className="facility-edit-input_container">
                    <Input
                      value={facilityState.full_name ?? ""}
                      id="full_name"
                      onChange={handleInputChange}
                      placeholder={t("Full name")}
                      label={t("Full Name")}
                      disabled={!permissions?.facility_details_edit}
                    />
                  </span>
                  <Input
                    value={facilityState.short_name ?? ""}
                    id="short_name"
                    onChange={handleInputChange}
                    placeholder={t("Short name")}
                    label={t("Short Name")}
                    disabled={!permissions?.facility_details_edit}
                  />
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
            <Card.Section title={t("Address Lines")}>
              <FormLayout>
                <FormLayout.Group>
                  <span className="facility-edit-input_container">
                    <Input
                      value={facilityState.address_line_1 ?? ""}
                      id="address_line_1"
                      onChange={handleInputChange}
                      placeholder={t("Address Line 1")}
                      label={t("Primary Address")}
                      disabled={!permissions?.facility_details_edit}
                    />
                  </span>
                  <span className="facility-edit-input_container">
                    <Input
                      value={facilityState.address_line_2 ?? ""}
                      id="address_line_2"
                      onChange={handleInputChange}
                      placeholder={t("Address Line 2")}
                      label={t("Secondary Address")}
                      disabled={!permissions?.facility_details_edit}
                    />
                  </span>
                </FormLayout.Group>
                <FormLayout.Group>
                  <span className="facility-edit-input_container">
                    <Input
                      value={facilityState.city ?? ""}
                      id="city"
                      onChange={handleInputChange}
                      placeholder={t("City")}
                      label={t("City")}
                      disabled={!permissions?.facility_details_edit}
                    />
                  </span>
                  <span className="facility-edit-input_container">
                    <Input
                      value={facilityState.province_name ?? ""}
                      id="province_name"
                      onChange={handleInputChange}
                      placeholder={t("Province")}
                      label={t("Province")}
                      disabled={!permissions?.facility_details_edit}
                    />
                  </span>
                  <span className="facility-edit-input_container">
                    <Input
                      value={facilityState.postal ?? ""}
                      id="postal"
                      onChange={handleInputChange}
                      placeholder={t("Postal Code")}
                      label={t("Postal Code")}
                      disabled={!permissions?.facility_details_edit}
                      error={!postcodeValidator(facilityState.postal, facilityState.country_code)}
                    />
                  </span>
                </FormLayout.Group>
                <FormLayout.Group>
                  <Input label={t("Latitude")} id="latitude" type={"number"} value={facilityState.latitude} onChange={handleInputChange} disabled={!permissions?.facility_details_edit} placeholder={`${t("e.g.")} 43.4520633`}/>
                  <Input label={t("Longitude")} id="longitude" type={"number"} value={facilityState.longitude} onChange={handleInputChange} disabled={!permissions?.facility_details_edit} placeholder={`${t("e.g.")} -80.513726`}/>
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>

            <Card.Section title={t("Time Zone")}>
              <FormLayout>
                <FormLayout.Group>
                  <span className="facility-edit-input_container">
                    <Select
                      label={t("Select Time Zone")}
                      onChange={(value: any) => handleTimeZoneChange(value)}
                      defaultValue={facilityState.timezone}
                      disabled={!permissions?.facility_details_edit}
                    >
                      {timeZones.map((timezone: any, index: number) => {
                        return (
                          <Option key={index} value={timezone} name={timezone}>
                            {timezone}
                          </Option>
                        );
                      })}
                    </Select>
                  </span>

                  <span></span>
                  <span></span>
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>

            <Card.Section title={t("Contact Information")}>
              <FormLayout>
                <FormLayout.Group>
                  <span className="facility-edit-input_container">
                    <Input
                      value={facilityState.phone ?? ""}
                      id="phone"
                      onChange={handleInputChange}
                      placeholder={t("Phone")}
                      label={t("Telephone")}
                      disabled={!permissions?.facility_details_edit}
                    />
                  </span>
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>

          <Card>
            <Card.Section title={t("Analytic Tracking Codes")}>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    value={facilityState.google_analytics_code ?? ""}
                    onChange={e =>
                      setFacilityState(prevState => ({ ...prevState, google_analytics_code: e.target.value }))
                    }
                    label={t("Google Analytics Code")}
                    disabled={!permissions?.facility_details_edit}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Input
                    value={facilityState.facebook_pixel_code ?? ""}
                    onChange={e =>
                      setFacilityState(prevState => ({ ...prevState, facebook_pixel_code: e.target.value }))
                    }
                    label={t("Facebook Pixel Code")}
                    disabled={!permissions?.facility_details_edit}
                  />
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>

          <Card>
            <Card.Section title={t("Images")}>
              <div className="product-image_grid">
                {facilityState.logo_source !== null || "" || undefined ? (
                  <div>
                    <div className="product-image_container">
                      <div className="product-image">
                        <div className="remove-button_container">
                          <div
                            className="remove-button"
                            onClick={() => (permissions?.facility_details_edit ? deleteFacilityImage() : undefined)}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </div>
                        </div>
                        <img src={facilityState.logo_source} alt={t('product image')} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="product-image-upload_container">
                      <div className="product-image-upload_content">
                        <input
                          ref={imageUploadInputRef}
                          className="product-image-upload_input"
                          type="file"
                          accept="image/*"
                          onChange={imageUploadOnChange}
                          disabled={!permissions?.facility_details_edit}
                        />
                        <p>
                          <FontAwesomeIcon icon={faUpload} />
                        </p>
                      </div>
                    </div>
                    <div className="product-image-upload_content"></div>
                  </>
                )}
              </div>
            </Card.Section>
          </Card>
          <Card>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Select
                    label={t("Default Currency")}
                    onChange={(value: number) => handleDropDownChange(value, "default_currency_id")}
                    defaultValue={facilityState?.default_currency_id}
                    disabled={!permissions?.facility_details_edit}
                  >
                    {currencies?.map((currency, index: number) => {
                      return (
                        <Option key={index} value={currency.id} name={currency.code}>
                          ({currency.symbol}) {currency.code?.toUpperCase()}
                        </Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>
                <FormLayout.Group>
                  <span className="facility-edit-input_container">
                    <Select
                      label={t("Merchant Code")}
                      onChange={(value: any) => handleDropDownChange(value, "merchant_code")}
                      defaultValue={facilityState.merchant_code}
                      disabled={!permissions?.facility_details_edit}
                    >
                      {business_types.map((business: any, index: number) => {
                        return (
                          <Option key={index} value={business.value} name={business.title}>
                            {business.title}
                          </Option>
                        );
                      })}
                    </Select>
                  </span>
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>
          <div className="pt-35"></div>
        </Form>
      ) : (
        <div style={{ height: "32px " }}>
          <Spin />
        </div>
      )}
    </Page>
  );
}
